import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Search,
  ChevronDown,
  Check,
  X,
  Loader2,
  Filter,
  ArrowUpDown,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CodeManagerSortOption = "Standard" | "Lex" | "Id_based";

export type FilterType = {
  key: string;
  name: string;
  description?: string;
  code?: string | number;
  raw?: any;
};

export interface FilterTypeSearchProps {
  filterTypeData: FilterType[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  value: string;
  onChange: (val: string) => void;
  onSortChange?: (sort: CodeManagerSortOption) => void;
  initialSort?: CodeManagerSortOption;
  placeholder?: string;
  width?: string | number;
  className?: string;
  disabled?: boolean;
}

const SORT_OPTIONS: { label: string; value: CodeManagerSortOption; description: string }[] = [
  { label: "Standard", value: "Standard", description: "Default order" },
  { label: "Alphabetical", value: "Lex", description: "Sort alphabetically (A-Z)" },
  { label: "By IDs", value: "Id_based", description: "Sort by IDs/Keys" },
];

export function FilterTypeSearch({
  filterTypeData = [],
  isLoading = false,
  onSearch,
  value,
  onChange,
  onSortChange,
  initialSort = "Standard",
  placeholder = "Select type",
  width,
  className,
  disabled = false,
}: FilterTypeSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sortBy, setSortBy] = useState<CodeManagerSortOption>(initialSort);

  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchInput.trim());
      if (onSearch) {
        onSearch(searchInput.trim());
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, onSearch]);

  const handleSortChange = (newSort: CodeManagerSortOption) => {
    setSortBy(newSort);
    if (onSortChange) {
      onSortChange(newSort);
    }
  };

  // Sort and filter the items
  const sortedAndFilteredItems = useMemo(() => {
    let list = [...(filterTypeData || [])];

    // Substring filter in case the caller returned unfiltered list
    if (debouncedQuery) {
      const q = debouncedQuery.toLowerCase();
      list = list.filter(
        (item) =>
          item.name?.toLowerCase().includes(q) ||
          item.key?.toLowerCase().includes(q)
      );
    }

    if (sortBy === "Lex") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "Id_based") {
      list.sort((a, b) =>
        String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true })
      );
    }

    return list;
  }, [filterTypeData, debouncedQuery, sortBy]);

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [open]);

  const handleSelect = (item: FilterType) => {
    onChange(item.name);
    setOpen(false);
  };

  const handleSelectAll = () => {
    onChange("ALL");
    setOpen(false);
  };

  const handleSelectCustomQuery = () => {
    if (debouncedQuery) {
      onChange(debouncedQuery);
      setOpen(false);
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("ALL");
  };

  // Find selected label
  const selectedLabel = useMemo(() => {
    if (!value || value === "ALL") return "";
    const matched = filterTypeData.find((f) => f.name === value || f.key === value);
    return matched ? matched.name : value;
  }, [value, filterTypeData]);

  const isExactMatchPresent = useMemo(() => {
    if (!debouncedQuery) return true;
    const lower = debouncedQuery.toLowerCase();
    return sortedAndFilteredItems.some((w) => w.name.toLowerCase() === lower);
  }, [debouncedQuery, sortedAndFilteredItems]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          style={width ? { width } : undefined}
          className={cn(
            "flex h-10 w-[450px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-left transition-colors hover:bg-accent/40",
            className
          )}
        >
          <span
            className={cn(
              "truncate min-w-0 mr-2",
              selectedLabel ? "font-medium text-foreground" : "text-muted-foreground"
            )}
          >
            {selectedLabel || placeholder}
          </span>

          <div className="flex items-center space-x-1 shrink-0 ml-auto">
            {selectedLabel && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClearSelection}
                className="p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                open && "transform rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] p-0 shadow-lg border border-border bg-popover rounded-md overflow-hidden z-50"
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-3 py-2 border-b border-border bg-muted/30">
          <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search type..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground text-foreground"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500 ml-1.5 shrink-0" />
          )}
        </div>

        {/* Order By (Sort) Toolbar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border/60 text-xs">
          <span className="flex items-center text-[11px] font-medium text-muted-foreground shrink-0 mr-2">
            <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground/70" />
            Order by:
          </span>
          <div className="flex items-center space-x-1 overflow-x-auto py-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSortChange(opt.value)}
                title={opt.description}
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium transition-all whitespace-nowrap",
                  sortBy === opt.value
                    ? "bg-workfinity-500 text-white shadow-xs font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Options */}
        <div className="border-b border-border/60 bg-muted/10 p-1">
          <button
            type="button"
            onClick={handleSelectAll}
            className={cn(
              "w-full flex items-center justify-between px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left font-medium",
              !value || value === "ALL"
                ? "bg-workfinity-50 text-workfinity-700 font-semibold"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <span>All types</span>
            {(!value || value === "ALL") && <Check className="h-3.5 w-3.5 text-workfinity-600" />}
          </button>

          {debouncedQuery && !isExactMatchPresent && (
            <button
              type="button"
              onClick={handleSelectCustomQuery}
              className="w-full flex items-center px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left text-workfinity-700 hover:bg-workfinity-50 font-medium"
            >
              <Filter className="h-3 w-3 mr-1.5 shrink-0" />
              <span className="truncate">
                Filter as &ldquo;<span className="underline">{debouncedQuery}</span>&rdquo;
              </span>
            </button>
          )}
        </div>

        {/* Scrollable Types List */}
        <div className="max-h-[260px] overflow-y-auto p-1 divide-y divide-border/20 text-sm">
          {isLoading && sortedAndFilteredItems.length === 0 && (
            <div className="py-6 text-center text-muted-foreground space-y-2">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-workfinity-500" />
              <p className="text-xs">Searching types...</p>
            </div>
          )}

          {!isLoading && sortedAndFilteredItems.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              <p className="text-xs">No types found.</p>
            </div>
          )}

          {sortedAndFilteredItems.map((item) => {
            const isSelected = value === item.name || value === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSelect(item)}
                className={cn(
                  "w-full flex items-center justify-between px-2.5 py-2 rounded-sm text-left transition-colors group",
                  isSelected
                    ? "bg-workfinity-50 text-workfinity-800 font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <div className="flex flex-col min-w-0 mr-2">
                  <div className="flex items-center space-x-1.5">
                    <span className="truncate font-medium">{item.name}</span>
                    {item.key && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground shrink-0 border border-border/40 font-mono">
                        {item.key}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <span className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {item.description}
                    </span>
                  )}
                </div>

                {isSelected && (
                  <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-1" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
